<template>
  <section>
    <v-slide-y-transition group>
      <WooriRequestFileGuide
        v-if="isWooriCrowd && isFileEditable"
        key="woori-guide"
        class="mt-n2 mb-5"
      />

      <v-expansion-panels v-if="files" multiple v-model="openPanels" key="files">
        <template v-for="(fileGroup, groupIndex) in fileGroups">
          <!-- 파일 없는 경우 -->
          <!-- 타이틀이 없으면 기타파일로 분류 기타파일은 언제든지 추가 가능 -->
          <RequestFileUploadButton
            :key="groupIndex"
            v-if="fileGroup.files.length === 0"
            :buttonText="fileGroup.buttonText"
            :button-description="fileGroup.description"
            :uuid="request.uuid"
            :title="fileGroup.title"
            class="width-100 mb-3"
            :disabled="disabled || fileGroup.disabled || (fileGroup.title && !isFileEditable)"
            @upload="upload(groupIndex)"
          />

          <!-- 파일 있는 경우 -->
          <v-expansion-panel
            :key="groupIndex"
            :ref="'panel-' + groupIndex"
            v-else
            class="rounded-4 width-100 mb-3"
          >
            <v-expansion-panel-header style="height: 64px" class="font--m-3" disable-icon-rotate>
              <div>
                <h3
                  class="font--m-3"
                  :class="{ 'color--disabled': fileGroup.title && !isFileEditable }"
                >
                  {{ fileGroup.buttonText }}
                </h3>
                <p
                  v-if="fileGroup.description"
                  class="mb-0 font--m-7 font-weight-bold"
                  :class="isFileEditable ? 'color--nice-grey' : 'color--disabled'"
                  style="margin-top: 3px"
                >
                  {{ fileGroup.description }}
                </p>
              </div>
              <template v-slot:actions>
                <v-icon v-if="fileGroup.title" color="accent"> mdi-check </v-icon>
                <v-icon v-else> mdi-plus </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list two-line class="py-0">
                <template v-for="(file, fileIndex) in fileGroup.files">
                  <v-divider :key="fileIndex + '-divider'"></v-divider>
                  <v-list-item :key="fileIndex + '-item'" class="px-0">
                    <!-- 파일 미리보기 아이콘 -->
                    <v-list-item-avatar tile>
                      <v-icon
                        v-if="extractFileExtension(file.file) === 'pdf'"
                        large
                        color="#f40f02"
                      >
                        mdi-file-pdf-box
                      </v-icon>
                      <v-img v-else-if="isImageFile(file.file)" contain :src="file.fileUrl"></v-img>
                    </v-list-item-avatar>

                    <!-- 파일 보기 링크 및 정보 -->
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold primary--text">
                        <a target="_blank" :href="file.fileUrl">
                          {{ extractFileName(file.file) }}
                        </a>
                      </v-list-item-title>
                      <v-list-item-subtitle class="color--nice-grey">
                        {{ file.created | moment('YYYY년 MM월 DD일 HH:mm') }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <!-- 파일 삭제 -->
                    <!-- 승인 후에는 유저가 삭제 불가 -->
                    <v-list-item-action v-if="isFileEditable">
                      <v-btn icon @click.stop="deleteFile(file)">
                        <v-icon color="error">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-list>

              <!-- 파일 추가 -->
              <!-- multiple 등록이 허용된 그룹에서만 추가 가능 -->
              <!-- 타이틀이 지정된 파일의 경우 승인 전에만 추가 가능 -->
              <!-- 타이틀이 없으면 기타파일로 분류 기타파일은 언제든지 추가 가능 -->
              <RequestFileAppendButton
                v-if="fileGroup.multiple && (!fileGroup.title || isFileEditable)"
                :uuid="request.uuid"
                :title="fileGroup.title"
                @upload="upload(groupIndex)"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </template>
      </v-expansion-panels>
    </v-slide-y-transition>
  </section>
</template>

<script>
import { extractFileExtension, extractFileName, isImageFile } from '@/util'

export default {
  props: {
    uuid: {
      Type: String,
      required: true,
    },
    request: {
      Type: Object,
      required: true,
    },
    /**
     * [{
     *    title: '사업자등록증'
     *    required(선택)
     *    description: ''(선택)
     * }]
     */
    fileCategory: {
      Type: Array,
      required: false,
      default: [],
    },
    requestFiles: {
      Type: Array,
      required: false,
    },
    disabled: {
      Type: Boolean,
      default: false,
    },
  },
  components: {
    RequestFileUploadButton: () => import('./RequestFileUploadButton.vue'),
    RequestFileAppendButton: () => import('./RequestFileAppendButton.vue'),
    WooriRequestFileGuide: () =>
      import('@/components/loans-request/detail/woori-crowd/WooriRequestFileGuide.vue'),
  },
  data() {
    return {
      files: null,
      openPanels: [],
    }
  },
  watch: {
    files: {
      deep: true,
      handler(v) {
        this.$emit('update:requestFiles', v)
      },
    },
  },
  computed: {
    /**
     * 파일 업로드 또는 삭제 가능 여부
     * 승인 이전에는 가능
     */
    isFileEditable() {
      return !['approve', 'reject', 'cancel'].includes(this.request.status)
    },

    // 파일을 file.title에 따라 그룹핑해서 보여줌
    fileGroups() {
      const groupsByTitle = this.fileCategory.map(
        ({ title, multiple, required, description, disabled }) => {
          const file = this.files.find((f) => f.title === title)

          console.log('file', file, title, multiple, required, description, disabled)

          return {
            title,
            buttonText: `${title} 제출${required ? ' (필수)' : ''}`,
            multiple,
            required,
            description,
            disabled,
            files: file ? [file] : [],
          }
        },
      )

      const etcGroup = {
        buttonText: '기타 서류 제출',
        multiple: true,
        files: this.files.filter(
          (f) => !this.fileCategory.find((category) => category.title === f.title),
        ),
      }

      return [...groupsByTitle, etcGroup]
    },

    /**
     * 우리동네 크라우드 신청건인지 여부
     */
    isWooriCrowd() {
      return this.request.type === 'woori_crowd'
    },
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    /**
     * 파일 목록 조회
     */
    async fetchData() {
      const { data } = await this.$defaultApi.getLoansRequestFiles(this.uuid)
      this.files = data
    },

    /**
     * 파일 추가
     * @param {*} fileGroupIndex 패널 펼치는용
     */
    async upload(fileGroupIndex) {
      await this.fetchData()

      // 패널 펼치기
      setTimeout(() => {
        const panel = this.$refs[`panel-${fileGroupIndex}`][0]
        if (panel && !panel.isActive) {
          panel.toggle()
        }
      }, 100)
    },

    /**
     * 파일 삭제
     * @param {*} file
     */
    async deleteFile(file) {
      await this.$defaultApi.deleteLoansRequestFile(file.uuid)
      await this.fetchData()
    },

    extractFileExtension,
    extractFileName,
    isImageFile,
  },
}
</script>

<style lang="scss" scoped>
.v-expansion-panel:not(:first-child)::after {
  border-top: none;
}
.v-expansion-panel--active:not(:first-child),
.v-expansion-panel--active + .v-expansion-panel {
  margin-top: 0;
}
</style>
